import { styled, Button, Tooltip, Menu, MenuItem, CircularProgress } from '@mui/material';
import FlexBetween from 'components/flexbox/FlexBetween';
import FlexBox from 'components/flexbox/FlexBox';
import SearchInput from 'components/input-fields/SearchInput';
import React, { useState } from 'react';
import axiosInstance from 'utils/axios';
import * as FileSaver from 'file-saver';
import { Download, ExpandMore } from '@mui/icons-material';
import downloadBatchPreview from 'utils/downloadBatchPreview';
import toast from 'react-hot-toast';

const SecondaryWrapper = styled(FlexBetween)(({ theme }) => ({
    gap: 8,
    flexWrap: 'wrap',
    margin: '24px 0',
    [theme.breakpoints.down(550)]: {
        '& .MuiInputBase-root': {
            maxWidth: 'calc(100% - 90px)',
        },
    },
})); 

const SearchArea = ({
    value,
    onChange,
    setValue,
    typeId,
    header,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [batchDownloading, setBatchDownloading] = useState(false);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleBatchDownload = async (selectedTypeId, label) => {
        handleMenuClose();
        setBatchDownloading(true);
        try {
            await downloadBatchPreview(selectedTypeId, label);
        } catch (err) {
            console.error(err);
            toast.error(err?.message || 'Failed to download batch preview.');
        } finally {
            setBatchDownloading(false);
        }
    };

    return (
        <SecondaryWrapper>
            <SearchInput
                placeholder="Search..."
                value={value || ''}
                onChange={(e) => {
                    if (setValue) setValue(e.target.value);
                    if (onChange) onChange(e.target.value);
                }}
            />

            <FlexBox alignItems="center" gap={1}>
                {/* Download Data (Excel) */}
                <Tooltip title="Download Data as Excel">
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ cursor: 'pointer', fontSize: 14 }}
                        endIcon={<Download />}
                        onClick={async () => {
                            try {
                                const res = await axiosInstance.get(
                                    '/api/prv/operator-submissions/download',
                                    {
                                        params: {
                                            status: 'approved',
                                            type_id: typeId,
                                        },
                                        responseType: 'blob',
                                    },
                                );
                                const blob = new Blob([res.data], {
                                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                });
                                FileSaver.saveAs(blob, 'Approved Abstract.xlsx');
                            } catch (err) {
                                console.log(err);
                            }
                        }}
                    >
                        Download Data
                    </Button>
                </Tooltip>

                {/* Download Preview (Batch Docx + Zip) */}
                <Tooltip title="Download Preview as ZIP of DOCX files">
                    <Button
                        variant="contained"
                        color="info"
                        style={{ cursor: 'pointer', fontSize: 14 }}
                        endIcon={batchDownloading ? <CircularProgress size={16} color="inherit" /> : <ExpandMore />}
                        disabled={batchDownloading}
                        onClick={handleMenuOpen}
                    >
                        Download Preview
                    </Button>
                </Tooltip>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {/* "All" option */}
                    <MenuItem onClick={() => handleBatchDownload('all', 'All')}>
                        All Types
                    </MenuItem>

                    {/* One option per submission type */}
                    {header && header.map((item) => (
                        <MenuItem
                            key={item.id}
                            onClick={() => handleBatchDownload(item.id, item.name)}
                        >
                            {item.name}
                        </MenuItem>
                    ))}
                </Menu>
            </FlexBox>
        </SecondaryWrapper>
    );
};

export default SearchArea;
